// agent-experience-redesign-v1.md §20.4 / §20.12 — auto-derive
// (gradient, glyph) for a marketplace template tile. Mirrors
// iconForAgent's shape but seeded from `template.category` first
// (since templates carry a freer-form category than agents) and
// falls back to `template.template` slug, then to a generic
// catalog icon.
//
// Per §20.12 Q2: the 12 domain templates from iconForAgent are
// the DEFAULT categories. Templates can also carry user-defined
// category strings (added via PublishTemplateModal "+ New
// category"); those fall through to a deterministic fallback
// gradient + glyph derived from the category string itself.

const CATEGORY_TO_TILE = {
  // 12 domain defaults — display-label friendly
  "legal-contracts":    { category: "supervisor",  iconName: "kb.book"          },
  "legal":              { category: "supervisor",  iconName: "kb.book"          },
  "healthcare":         { category: "campaigns",   iconName: "feature.tester"   },
  "network-telecom":    { category: "channels",    iconName: "node.api"         },
  "code":               { category: "builder",     iconName: "block.code"       },
  "it-helpdesk":        { category: "agents",      iconName: "tab.admin"        },
  "finance-accounting": { category: "campaigns",   iconName: "feature.spark"    },
  "finance":            { category: "campaigns",   iconName: "feature.spark"    },
  "supply-chain":       { category: "schedules",   iconName: "node.workflow"    },
  "hr-people-ops":      { category: "marketplace", iconName: "user.profile"     },
  "hr":                 { category: "marketplace", iconName: "user.profile"     },
  "project-management": { category: "supervisor",  iconName: "feature.bookmark" },
  "sales-crm":          { category: "marketplace", iconName: "tab.campaigns"    },
  "sales":              { category: "marketplace", iconName: "tab.campaigns"    },
  "engineering-devops": { category: "builder",     iconName: "block.code"       },
  "generic":            { category: "agents",      iconName: "tab.agents"       },

  // Legacy categories from the existing MarketplaceView (preserved
  // so old templates don't lose their visual identity on overhaul)
  "customer-support":   { category: "channels",    iconName: "channel.chat"     },
  "writing":            { category: "supervisor",  iconName: "kb.bookopen"      },
  "research":           { category: "analytics",   iconName: "kb.search"        },
};

// Friendly display labels for the 12 domain slugs. Anything not in
// this map renders the slug verbatim (which is the right behavior
// for user-defined categories).
const CATEGORY_LABELS = {
  "legal-contracts":    "Legal & Contracts",
  "healthcare":         "Healthcare",
  "network-telecom":    "Network & Telecom",
  "code":               "Code",
  "it-helpdesk":        "IT Helpdesk",
  "finance-accounting": "Finance & Accounting",
  "supply-chain":       "Supply Chain",
  "hr-people-ops":      "HR & People Ops",
  "project-management": "Project Management",
  "sales-crm":          "Sales & CRM",
  "engineering-devops": "Engineering & DevOps",
  "generic":            "Generic",
  // Legacy
  "customer-support":   "Customer Support",
  "writing":            "Writing",
  "research":           "Research",
  "sales":              "Sales",
};

// Default-category list shown in the chip strip even when zero
// templates currently use them — so an empty marketplace still
// telegraphs the platform's domain coverage. The chip strip
// merges this with non-default categories that ARE in use.
export const DEFAULT_CATEGORIES = [
  "legal-contracts",
  "healthcare",
  "network-telecom",
  "code",
  "it-helpdesk",
  "finance-accounting",
  "supply-chain",
  "hr-people-ops",
  "project-management",
  "sales-crm",
  "engineering-devops",
  "generic",
];

// Fallback gradients used when a user-defined category has no entry
// in CATEGORY_TO_TILE. Picked deterministically by hashing the
// category string so the same custom name always renders the same
// gradient (no color flicker between visits).
const FALLBACK_CATEGORIES = ["builder", "agents", "channels", "campaigns", "schedules", "supervisor", "analytics", "marketplace"];
function hashString(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/**
 * Resolve (category, iconName) for a marketplace template.
 *
 * Resolution order:
 *   1. template.icon_override         — explicit author override (rare)
 *   2. CATEGORY_TO_TILE[category]     — 12 domain defaults + legacy
 *   3. CATEGORY_TO_TILE[template]     — fallback to template slug
 *   4. fallback gradient + tab.marketplace — for user-defined categories
 */
export function resolveTemplateIcon(template) {
  if (!template) return { category: "marketplace", iconName: "tab.marketplace" };

  // Explicit override on the template row (rare; not in current schema
  // but supported for forward-compat)
  if (template.icon_override && typeof template.icon_override === "object") {
    return {
      category: template.icon_override.category || "marketplace",
      iconName: template.icon_override.iconName || "tab.marketplace",
    };
  }

  const cat = template.category;
  if (cat && CATEGORY_TO_TILE[cat]) return CATEGORY_TO_TILE[cat];

  const slug = template.template;
  if (slug && CATEGORY_TO_TILE[slug]) return CATEGORY_TO_TILE[slug];

  // User-defined category (or unmapped) — deterministic fallback
  const seed = cat || slug || "marketplace";
  const idx = hashString(seed) % FALLBACK_CATEGORIES.length;
  return { category: FALLBACK_CATEGORIES[idx], iconName: "tab.marketplace" };
}

/**
 * Friendly display label for a category slug. Unknown slugs return
 * the slug Title-Cased with dashes/underscores as spaces, so user-defined
 * categories like "billing-disputes" render as "Billing Disputes" rather
 * than the raw slug.
 */
export function categoryLabel(slug) {
  if (!slug) return "Uncategorized";
  if (CATEGORY_LABELS[slug]) return CATEGORY_LABELS[slug];
  // Convert kebab/snake to Title Case (first letter of every word upper-cased)
  return slug
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Build the union of (default categories + non-default categories
 * actually in use) for the chip strip. Sorted: defaults first in
 * canonical order, custom categories alphabetically after.
 */
export function buildCategoryStrip(templates) {
  const inUse = new Set();
  for (const t of (templates || [])) {
    if (t.category) inUse.add(t.category);
  }
  const defaults = DEFAULT_CATEGORIES.filter(d => true); // show all defaults
  const custom = [...inUse]
    .filter(c => !DEFAULT_CATEGORIES.includes(c))
    .sort((a, b) => a.localeCompare(b));
  return [...defaults, ...custom];
}
